import React, { useState } from 'react';

/**
 * SharedComponents.js
 * Design system primitives for the Procurement Intelligence Platform
 * Session 31: Design audit overhaul
 *
 * Changes from previous version:
 * - Color palette updated (deeper neutrals, better semantic colors)
 * - Typography tightened (24px titles, 14px body, 12px labels)
 * - Cards have subtle borders instead of heavy shadows
 * - Tables have lighter header treatment, better row spacing
 * - Buttons follow 4-tier system (primary/secondary/ghost/danger)
 * - Badges use proper background tints, not hex+opacity hacks
 * - DataTable rows have smooth hover, no heavy borders
 */

// ─── COLOR PALETTE ──────────────────────────────────────────────
//
// SINGLE SOURCE OF TRUTH (R2, Lane 77): App.css :root is the canonical color
// source. Each value below is a byte-identical MIRROR of the CSS custom property
// named in its comment ("-> --token"). The mirror is ENFORCED by
// SharedComponents.colorTokens.test.js -- CI fails if any mapped value here ever
// diverges from App.css :root, so a color must be changed in BOTH places together.
//
// Values are literal hex (NOT `var(--token)` references) on purpose: some
// consumers set color via bare SVG presentation attributes (e.g.
// `stroke={colors.textMuted}`), where CSS var() does NOT resolve and would fall
// back to black. Literal hex renders identically in both CSS and SVG contexts.
//
// As of R4 (Lane 79) every value below is the Meridian palette (docs/design/DESIGN.md).
// 21 keys mirror a CSS token ("-> --token"); ONE key (deepBlue, the buyer sidebar
// dark) has no CSS twin yet and stays FROZEN pending R6 (portal chrome). The mirror
// is enforced by SharedComponents.colorTokens.test.js.
export const colors = {
  // Brand -- Meridian (R4, Lane 79)
  electricBlue: '#2B54E6',                // -> --color-brand
  electricBlueHover: '#1E3FBF',           // -> --color-brand-hover
  brandLight: '#EDF0FE',                  // -> --color-brand-light
  brandMuted: 'rgba(43, 84, 230, 0.08)',  // -> --color-brand-muted
  cyan: '#0EA5C4',                        // -> --color-info (info unified to teal, R4)

  // Neutrals -- Meridian (R4, Lane 79)
  deepBlue: '#0F172A',                    // FROZEN / no CSS twin: buyer sidebar dark -- pending R6 (DESIGN.md §3.2 --sidebar-bg is #16181D)
  darkText: '#16181D',                    // -> --color-text-primary
  secondaryText: '#6B7280',               // -> --color-text-secondary (Decision 1; #4B5563 retired)
  textMuted: '#767B85',                   // -> --color-text-muted
  placeholder: '#9AA0AA',                 // -> --color-text-placeholder
  white: '#FFFFFF',                       // -> --color-surface
  lightGray: '#F8F9FB',                   // -> --color-bg (Decision 2; #F7F8FA retired)
  mediumGray: '#E5E7EB',                  // -> --color-border
  borderLight: '#F0F1F3',                 // -> --color-border-light

  // Semantic -- Meridian (R4, Lane 79)
  success: '#08875D',                     // -> --color-success
  successLight: '#E3F3EC',                // -> --color-success-light
  warning: '#B8730B',                     // -> --color-warning
  warningLight: '#F6EEDC',                // -> --color-warning-light
  danger: '#D23B34',                      // -> --color-danger
  dangerLight: '#FBE7E5',                 // -> --color-danger-light
  purple: '#7C3AED',                      // -> --admin-accent
  purpleLight: '#F3F0FF',                 // -> --admin-accent-light
};

// ─── SUPPLIER COLOR PALETTE ────────────────────────────────────
// 10-color palette for deterministic supplier color assignment.
// Sorted alphabetically so the same supplier always gets the same color.
export const SUPPLIER_COLORS = [
  '#2251FF', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444',
  '#06B6D4', '#EC4899', '#6366F1', '#14B8A6', '#F97316',
];

export function getSupplierColorMap(supplierNames) {
  const sorted = [...new Set(supplierNames)].sort((a, b) => a.localeCompare(b));
  const map = {};
  sorted.forEach((name, i) => {
    map[name] = SUPPLIER_COLORS[i % SUPPLIER_COLORS.length];
  });
  return map;
}


// ─── PAGE HEADER ────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions, breadcrumb }) {
  return (
    <div style={{
      marginBottom: '24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    }}>
      <div>
        {breadcrumb && (
          <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            {breadcrumb}
          </div>
        )}
        <h1 style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          margin: '0 0 2px 0',
          letterSpacing: 'var(--letter-spacing-tight)',
          lineHeight: 'var(--line-height-tight)',
        }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 0,
            maxWidth: '600px',
            lineHeight: 'var(--line-height-relaxed)',
          }}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && (
        <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
          {actions}
        </div>
      )}
    </div>
  );
}


// ─── CARD ───────────────────────────────────────────────────────
export function Card({ children, padding = 'var(--space-lg)', style = {}, onClick, hover = false, className = '' }) {
  const [isHovered, setIsHovered] = React.useState(false);

  // Meridian card (DESIGN.md section 7): --color-surface bg, 1px --color-border,
  // --radius-lg (12px), resting --shadow-xs, 15-16px padding (default 16 =
  // --space-lg). The opt-in `hover` affordance shifts the border to the generic
  // --color-brand accent WITHOUT a shadow bump -- in keeping with Meridian's
  // "resting surfaces get a whisper shadow; only modals lift meaningfully"
  // elevation rule (section 6). --color-brand is referenced generically so the
  // R6 admin-shell remap (--color-brand -> --admin-accent) renders it purple in
  // the admin portal. These are CSS style props on a <div>, where var() resolves
  // (unlike the SVG-attribute consumers that require the literal-hex `colors`).
  return (
    <div
      className={className}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding,
        border: `1px solid ${isHovered && hover ? 'var(--color-brand)' : 'var(--color-border)'}`,
        boxShadow: 'var(--shadow-xs)',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  );
}


// ─── BUTTON ─────────────────────────────────────────────────────
export function Button({
  children,
  variant = 'primary',
  size = 'medium',
  icon,
  onClick,
  disabled = false,
  fullWidth = false,
  style = {},
}) {
  const [isHovered, setIsHovered] = React.useState(false);

  // Meridian buttons (DESIGN.md §7). Accent surfaces reference the --color-brand*
  // tokens GENERICALLY (never a literal hue), so the R6 admin-shell remap
  // (--color-brand -> --admin-accent) makes primary/ghost render purple in the
  // admin portal with zero per-portal styling. These are var() references (not
  // the literal-hex `colors` mirror, which exists only for SVG presentation
  // attributes) because the Button sets color via CSS style props, where var()
  // resolves -- and only var() picks up the admin remap.
  const variants = {
    primary: {
      backgroundColor: isHovered ? 'var(--color-brand-hover)' : 'var(--color-brand)',
      color: 'var(--color-surface)',
      border: '1px solid transparent',
      boxShadow: 'var(--shadow-brand)',
    },
    secondary: {
      // --text-secondary text; firms to 600 on hover (shared block below).
      backgroundColor: isHovered ? 'var(--color-surface-subtle)' : 'var(--color-surface)',
      color: 'var(--color-text-secondary)',
      border: '1px solid var(--color-border)',
      boxShadow: 'var(--shadow-xs)',
    },
    ghost: {
      backgroundColor: isHovered ? 'var(--color-brand-muted)' : 'transparent',
      color: 'var(--color-brand)',
      border: '1px solid transparent',
    },
    danger: {
      // white bg, danger-tinted border, --danger text (DESIGN.md §7). #F0CFCD is
      // the one §7 value with no :root token yet (candidate --color-danger-border
      // for a future token lane); fill/text use existing danger tokens. Solid-
      // danger is reserved for destructive-confirm inside modals, not this button.
      backgroundColor: isHovered ? 'var(--color-danger-light)' : 'var(--color-surface)',
      color: 'var(--color-danger)',
      border: '1px solid #F0CFCD',
    },
    // NOT in the Meridian §7 vocabulary and has zero <Button> callsites today
    // (the `variant="success"` usages are all <Badge>). Retained verbatim so a
    // dynamic variant prop can never fall through to an unstyled button; a future
    // lane should reconcile or remove it.
    success: {
      backgroundColor: isHovered ? '#047857' : colors.success,
      color: colors.white,
      border: `1px solid ${isHovered ? '#047857' : colors.success}`,
    },
  };

  // Meridian §7 sizes -- both 13px. 'medium' is the default (8x14); 'small' is
  // the compact tier (7x12). 'large' has no §7 equivalent -- kept proportional
  // for the single size="large" callsite (InterviewWizard completion screen).
  const sizes = {
    small: { padding: '7px 12px', fontSize: 'var(--font-size-base)' },
    medium: { padding: '8px 14px', fontSize: 'var(--font-size-base)' },
    large: { padding: '10px 20px', fontSize: 'var(--font-size-base)' },
  };

  const variantStyle = variants[variant] || variants.primary;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...variantStyle,
        ...sizes[size],
        borderRadius: 'var(--radius-md)',
        // Secondary sits at medium weight, firming to semibold on hover (§7);
        // every other variant is semibold.
        fontWeight: (variant === 'secondary' && !isHovered)
          ? 'var(--font-weight-medium)'
          : 'var(--font-weight-semibold)',
        minHeight: '32px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        // Disabled: 40% opacity, no shadow, not-allowed (§7).
        opacity: disabled ? 0.4 : 1,
        boxShadow: disabled ? 'none' : (variantStyle.boxShadow || 'none'),
        transition: 'all 0.15s ease',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: fullWidth ? '100%' : 'auto',
        fontFamily: 'inherit',
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {icon && <span style={{ fontSize: 'var(--font-size-base)' }}>{icon}</span>}
      {children}
    </button>
  );
}


// ─── BADGE ──────────────────────────────────────────────────────
// Meridian badges/pills (DESIGN.md §7 + §3.3): --radius-sm, 11px weight 600,
// 2-3px x 8-9px padding, each variant = its semantic -light fill + solid
// semantic text. Colors are var(--color-*) references (NOT the literal-hex
// `colors` mirror): a Badge sets color through CSS style props where var()
// resolves, so it reads the R4 Meridian token values -- and, for the brand
// accent, the future R6 admin-shell remap (--color-brand -> --admin-accent)
// with zero per-portal styling. "info" is now the Meridian teal
// (--color-info #0EA5C4 / --color-info-light #E1F4F8), replacing the old
// hardcoded cyan (#E0F7FA / #0097A7) that lived in no token layer.
//
// Optional `dot` (default off, so every existing call-site is unchanged):
// prepends a 6px solid-semantic dot for the inline-table risk-pill pattern
// (§7 "Risk pills/dots: Low->success, Med->warning, High->danger. 6px dot + label").
export function Badge({ children, variant = 'default', size = 'medium', dot = false }) {
  // Linear rebuild Stage 2 (Lane 219): scope-adaptive. Each variant reads the
  // unprefixed Linear token first, falling back to its Meridian token
  // (var(--good, var(--color-success)) etc.). Inside a Linear page scope a Badge
  // reads the Linear palette (matching the page's own `.sd-badge`/`.matd-badge`
  // tones — MaterialDossier's contract-clause pill is the live consumer); on the
  // ~35 non-Linear consumers (no Linear tokens defined) it stays byte-identical
  // Meridian. Where a scope defines only the solid tone (e.g. `.sd` has --good but
  // not --good-soft), the fill fallback lands on the Meridian -light tint, which
  // reads correctly under the slightly-shifted Linear text hue. The `colors`
  // object is unchanged (colorTokens drift-guard holds).
  const variants = {
    // Neutral (draft / pending) -- Linear .sd-badge.mut = --panel-2 fill / --text-2 text.
    // `secondary` shares the neutral pair (as it did before this restyle).
    default:   { backgroundColor: 'var(--panel-2, var(--color-border-light))',  color: 'var(--text-2, var(--color-text-secondary))' },
    secondary: { backgroundColor: 'var(--panel-2, var(--color-border-light))',  color: 'var(--text-2, var(--color-text-secondary))' },
    // Brand/accent (Linear --accent; Meridian --color-brand, which resolves to
    // admin purple inside the R6 admin shell).
    primary:   { backgroundColor: 'var(--accent-soft, var(--color-brand-light))',   color: 'var(--accent, var(--color-brand))' },
    // Semantic status pairs (Linear good/warn/crit/info; Meridian fallback).
    success:   { backgroundColor: 'var(--good-soft, var(--color-success-light))', color: 'var(--good, var(--color-success))' },
    warning:   { backgroundColor: 'var(--warn-soft, var(--color-warning-light))', color: 'var(--warn, var(--color-warning))' },
    danger:    { backgroundColor: 'var(--crit-soft, var(--color-danger-light))',  color: 'var(--crit, var(--color-danger))' },
    info:      { backgroundColor: 'var(--info-soft, var(--color-info-light))',    color: 'var(--info, var(--color-info))' },
  };

  // §7 badge padding is 2-3px x 8-9px at the 11px floor. The size prop varies
  // padding within that envelope; Meridian defines one badge font size
  // (--font-size-badge = 11px, the token the app's other badges already use), so
  // all tiers use it. `large` is a vestigial alias (0 call-sites) kept for API
  // stability -- Meridian has no distinct large badge.
  const sizes = {
    small:  { padding: '2px 8px', fontSize: 'var(--font-size-badge)' },
    medium: { padding: '3px 9px', fontSize: 'var(--font-size-badge)' },
    large:  { padding: '3px 9px', fontSize: 'var(--font-size-badge)' },
  };

  const variantStyle = variants[variant] || variants.default;

  return (
    <span style={{
      ...variantStyle,
      ...(sizes[size] || sizes.medium),
      borderRadius: 'var(--radius-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      letterSpacing: '0.02em',
      lineHeight: 1.5,
      whiteSpace: 'nowrap',
    }}>
      {dot && (
        <span aria-hidden="true" style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: variantStyle.color,
          flexShrink: 0,
        }} />
      )}
      {children}
    </span>
  );
}


// ─── INPUT ──────────────────────────────────────────────────────
export function Input({
  type = 'text',
  label,
  placeholder,
  value,
  onChange,
  icon,
  style = {},
  ...props
}) {
  // Meridian DESIGN.md section 7 "Inputs & selects" + section 6 focus-ring.
  // All color/border/radius/focus values are generic var(--*) tokens (never a
  // literal hue), so the R6 admin-shell remap resolves purple with no per-portal
  // styling. Placeholder color is already governed globally by App.css
  // `input::placeholder { color: var(--color-text-placeholder) }`.
  return (
    <div style={{ position: 'relative', ...style }}>
      {label && (
        <label style={{
          display: 'block',
          marginBottom: 'var(--space-sm)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--color-text-secondary)',
        }}>
          {label}
        </label>
      )}
      {/* icon centers on this inner row, not the label above it */}
      <div style={{ position: 'relative' }}>
        {icon && (
          <span style={{
            position: 'absolute',
            left: '11px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'inline-flex',
            alignItems: 'center',
            color: 'var(--color-text-placeholder)',
            fontSize: 'var(--font-size-base)',
            pointerEvents: 'none',
          }}>
            {icon}
          </span>
        )}
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          style={{
            width: '100%',
            padding: icon ? '8px 11px 8px 32px' : '8px 11px',
            fontSize: 'var(--font-size-base)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text-primary)',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            outline: 'none',
            lineHeight: 1.5,
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--color-brand)';
            e.target.style.boxShadow = 'var(--focus-ring)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'var(--color-border)';
            e.target.style.boxShadow = 'none';
          }}
          {...props}
        />
      </div>
    </div>
  );
}


// ─── SELECT ─────────────────────────────────────────────────────
export function Select({ value, onChange, options, placeholder, label, style = {}, disabled = false }) {
  // Meridian DESIGN.md section 7 "Inputs & selects" + section 6 focus-ring.
  // Generic var(--*) accent tokens (R6 admin remap resolves purple). Label-less
  // callsites get a bare <select> (byte-identical structure to before), with the
  // caller's `style` sizing the <select> directly. When a label is present, the
  // caller's `style` sizes the wrapper and the <select> fills its width.
  const selectStyle = {
    padding: '8px 11px',
    fontSize: 'var(--font-size-base)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: disabled ? 'var(--color-surface-subtle)' : 'var(--color-surface)',
    color: disabled ? 'var(--color-text-placeholder)' : 'var(--color-text-primary)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    outline: 'none',
    lineHeight: 1.5,
    opacity: disabled ? 0.7 : 1,
    ...(label ? { width: '100%' } : { minWidth: '150px', ...style }),
  };

  const selectEl = (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      style={selectStyle}
      onFocus={(e) => {
        if (disabled) return;
        e.target.style.borderColor = 'var(--color-brand)';
        e.target.style.boxShadow = 'var(--focus-ring)';
      }}
      onBlur={(e) => {
        e.target.style.borderColor = 'var(--color-border)';
        e.target.style.boxShadow = 'none';
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );

  if (!label) return selectEl;

  return (
    <div style={{ ...style }}>
      <label style={{
        display: 'block',
        marginBottom: 'var(--space-sm)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--color-text-secondary)',
      }}>
        {label}
      </label>
      {selectEl}
    </div>
  );
}


// ─── DATA TABLE ─────────────────────────────────────────────────
export function DataTable({ columns, data, onRowClick, emptyMessage = 'No data available', sortColumn, sortDirection, onSort, bordered = false }) {
  if (!data || data.length === 0) {
    return (
      <div style={{
        padding: '48px 24px',
        textAlign: 'center',
        color: colors.textMuted,
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          backgroundColor: colors.lightGray,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 12px',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colors.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <line x1="3" y1="9" x2="21" y2="9"/>
            <line x1="9" y1="21" x2="9" y2="9"/>
          </svg>
        </div>
        <div style={{ fontSize: 'var(--font-size-base)' }}>{emptyMessage}</div>
      </div>
    );
  }

  // Build title text for hover tooltip from cell value
  const getCellTitle = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    return '';
  };

  const inner = (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            {columns.map((col, idx) => {
              const isSortable = onSort && col.sortable !== false;
              const isActive = sortColumn === col.key;
              return (
                <th
                  key={idx}
                  onClick={isSortable ? () => onSort(col.key) : undefined}
                  style={{
                    textAlign: col.align || 'left',
                    padding: '9px 16px',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: isActive ? 'var(--color-brand)' : 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    borderBottom: '1px solid var(--color-border-light)',
                    backgroundColor: 'var(--color-surface-subtle)',
                    cursor: isSortable ? 'pointer' : 'default',
                    userSelect: isSortable ? 'none' : 'auto',
                    transition: 'color 0.15s ease',
                    width: col.width || 'auto',
                  }}
                  title={col.header}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    {col.header}
                    {isSortable && isActive && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        {sortDirection === 'asc'
                          ? <path d="M6 2.5L6 9.5M6 2.5L3 5.5M6 2.5L9 5.5" />
                          : <path d="M6 9.5L6 2.5M6 9.5L3 6.5M6 9.5L9 6.5" />
                        }
                      </svg>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIdx) => (
            <tr
              key={rowIdx}
              onClick={() => onRowClick && onRowClick(row)}
              style={{
                borderBottom: rowIdx === data.length - 1 ? 'none' : '1px solid var(--color-border-light)',
                cursor: onRowClick ? 'pointer' : 'default',
                transition: 'background-color 0.1s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-surface-subtle)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {columns.map((col, colIdx) => {
                // Meridian (DESIGN.md section 7): right-aligned columns are
                // numeric by convention -> tabular numerals so digits align.
                // A true monospace is opt-in per column via col.mono (default
                // off): switching a fixed-width numeric/ID column to monospace
                // shifts glyph widths and can ellipsis-truncate the densest
                // currency columns under tableLayout:'fixed', so it is deferred
                // to the per-page sweep where each column width is verified.
                const isNumeric = (col.align || 'left') === 'right';
                return (
                  <td
                    key={colIdx}
                    title={col.title ? col.title(row[col.key], row) : getCellTitle(row[col.key])}
                    style={{
                      padding: '11px 16px',
                      fontSize: 'var(--font-size-base)',
                      color: 'var(--color-text-primary)',
                      textAlign: col.align || 'left',
                      lineHeight: 1.4,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      ...((isNumeric || col.mono) ? { fontVariantNumeric: 'tabular-nums' } : {}),
                      ...(col.mono ? { fontFamily: "var(--font-mono, 'SF Mono', 'Fira Code', 'Fira Mono', monospace)" } : {}),
                    }}
                  >
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Meridian container (DESIGN.md section 7 "Tables"): --color-surface fill, 1px
  // --color-border, --radius-lg, whisper --shadow-xs, overflow-hidden to clip the
  // rounded corners; the inner overflowX:auto region preserves horizontal scroll
  // for wide/dense tables (never truncates). Opt-in via `bordered` (default OFF):
  // almost every call-site already renders DataTable inside a Meridian
  // Card/TableCard/bordered surface, so a default-on container would double-box
  // them. Bare tables (e.g. the two in OpportunityFinding) opt in during the
  // per-page sweep. Default OFF keeps every existing call-site byte-identical.
  if (bordered) {
    return (
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-xs)',
        overflow: 'hidden',
      }}>
        {inner}
      </div>
    );
  }

  return inner;
}


// ─── MODAL ──────────────────────────────────────────────────────
// Linear rebuild Stage 2 (Lane 219): scope-adaptive shell. The surface / border /
// hairline / title / close-button chrome read the unprefixed Linear tokens with
// the Meridian token as fallback, so a modal rendered inside a Linear page scope
// (SupplierDetail `.sd`, MaterialDossier `.matd` — whose contract-modal BODY Lane
// 215 already re-chromed to Linear) gets a matching Linear shell instead of a
// Meridian frame around Linear content; every other consumer stays byte-identical
// Meridian. The scrim + layout are unchanged; the `colors` object is untouched.
export function Modal({ isOpen, onClose, title, children, size = 'medium', maxWidth: maxWidthOverride }) {
  if (!isOpen) return null;

  const sizes = {
    small: '400px',
    medium: '560px',
    large: '720px',
    xlarge: '900px',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(16, 24, 40, 0.40)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '24px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--panel, var(--color-surface))',
          border: '1px solid var(--border, var(--color-border))',
          borderRadius: 'var(--radius-lg)',
          width: '100%',
          maxWidth: maxWidthOverride || sizes[size],
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-lg, var(--shadow-modal))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: 'var(--space-md) var(--space-xl)',
            borderBottom: '1px solid var(--hair, var(--color-border-light))',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{
            margin: 0,
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--text, var(--color-text-primary))',
            lineHeight: 1.2,
          }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: '28px',
              height: '28px',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-3, var(--color-text-placeholder))',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--panel-2, var(--color-surface-subtle))'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <line x1="3.5" y1="3.5" x2="10.5" y2="10.5" />
              <line x1="10.5" y1="3.5" x2="3.5" y2="10.5" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 'var(--space-xl)', overflowY: 'auto', flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}


// ─── EMPTY STATE ────────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  return (
    <div style={{
      padding: '40px 20px',
      textAlign: 'center',
    }}>
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '14px',
        backgroundColor: colors.lightGray,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
      }}>
        {icon ? (
          <span style={{ fontSize: 'var(--font-size-xl)' }}>{icon}</span>
        ) : (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={colors.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        )}
      </div>
      <h3 style={{
        fontSize: 'var(--font-size-base)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--color-text-primary)',
        margin: '0 0 6px 0',
      }}>
        {title}
      </h3>
      {description && (
        <p style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-muted)',
          margin: '0 0 20px 0',
          maxWidth: '360px',
          marginLeft: 'auto',
          marginRight: 'auto',
          lineHeight: 1.5,
        }}>
          {description}
        </p>
      )}
      {action}
    </div>
  );
}


// ─── LOADING SPINNER ────────────────────────────────────────────
// Linear rebuild Stage 2 (Lane 219): scope-adaptive. The ring/text read the
// unprefixed Linear tokens with the Meridian token as fallback
// (var(--accent, var(--color-brand)) etc.), so a spinner rendered INSIDE a
// Linear page scope (`.sd`/`.matd`, where LoadingSpinner is used) draws in the
// Linear accent, and every other consumer (admin/supplier/RFP — no Linear tokens
// defined) stays byte-identical Meridian. The `colors` object is unchanged.
export function LoadingSpinner({ size = 'medium', text }) {
  const sizes = { small: 20, medium: 32, large: 48 };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px',
    }}>
      <div
        style={{
          width: sizes[size],
          height: sizes[size],
          border: '2px solid var(--hair, var(--color-border-light))',
          borderTopColor: 'var(--accent, var(--color-brand))',
          borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
        }}
      />
      {text && (
        <div style={{ marginTop: '14px', color: 'var(--text-3, var(--color-text-muted))', fontSize: 'var(--font-size-sm)' }}>
          {text}
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


// ─── ERROR MESSAGE ─────────────────────────────────────────────
export function ErrorMessage({ message, onRetry }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 24px',
      textAlign: 'center',
    }}>
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={colors.danger} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '14px', opacity: 0.7 }}>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <div style={{ color: colors.darkText, fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', marginBottom: '6px' }}>
        {message || 'Something went wrong'}
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            marginTop: '10px',
            padding: '6px 16px',
            fontSize: 'var(--font-size-sm)',
            color: colors.electricBlue,
            background: 'none',
            border: `1px solid ${colors.electricBlue}`,
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      )}
    </div>
  );
}


// ─── TABS ───────────────────────────────────────────────────────
export function Tabs({ tabs, activeTab, onChange }) {
  return (
    <div style={{
      display: 'flex',
      gap: '0px',
      borderBottom: `1px solid ${colors.mediumGray}`,
      marginBottom: '16px',
    }}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            style={{
              padding: '8px 14px',
              fontSize: 'var(--font-size-sm)',
              fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
              color: isActive ? colors.electricBlue : colors.textMuted,
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${isActive ? colors.electricBlue : 'transparent'}`,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              marginBottom: '-1px',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.icon && <span style={{ marginRight: '6px' }}>{tab.icon}</span>}
            {tab.label}
            {tab.count !== undefined && (
              <span style={{
                marginLeft: '6px',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                color: isActive ? colors.electricBlue : colors.textMuted,
                backgroundColor: isActive ? colors.brandLight : colors.lightGray,
                padding: '1px 6px',
                borderRadius: '100px',
              }}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}


// ─── PAGINATION ─────────────────────────────────────────────────
export function Pagination({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  scrollTargetRef,
}) {
  const totalPages = Math.ceil(totalItems / pageSize);

  // Don't render when all items fit on one page
  if (totalItems <= pageSize) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    onPageChange(newPage);
    if (scrollTargetRef?.current) {
      scrollTargetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px',
      borderTop: `1px solid ${colors.borderLight}`,
      fontSize: 'var(--font-size-sm)',
      color: colors.textMuted,
      flexWrap: 'wrap',
      gap: '8px',
    }}>
      {/* Item count + page size selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span>Showing {startItem} to {endItem} of {totalItems} items</span>
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
              onPageChange(1);
            }}
            style={{
              padding: '4px 8px',
              fontSize: 'var(--font-size-sm)',
              border: `1px solid ${colors.mediumGray}`,
              borderRadius: '4px',
              backgroundColor: colors.white,
              color: colors.darkText,
              cursor: 'pointer',
            }}
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>{size} per page</option>
            ))}
          </select>
        )}
      </div>

      {/* Page controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: '4px 10px',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            border: `1px solid ${colors.mediumGray}`,
            borderRadius: '4px',
            backgroundColor: colors.white,
            color: currentPage === 1 ? colors.placeholder : colors.secondaryText,
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            opacity: currentPage === 1 ? 0.5 : 1,
            fontFamily: 'inherit',
          }}
        >
          Previous
        </button>

        {getPageNumbers().map((page, idx) => (
          page === '...' ? (
            <span key={`ellipsis-${idx}`} style={{ padding: '4px 6px', color: colors.textMuted }}>{'\u2026'}</span>
          ) : (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              style={{
                padding: '4px 10px',
                fontSize: 'var(--font-size-sm)',
                fontWeight: page === currentPage ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
                border: `1px solid ${page === currentPage ? colors.electricBlue : colors.mediumGray}`,
                borderRadius: '4px',
                backgroundColor: page === currentPage ? colors.brandLight : colors.white,
                color: page === currentPage ? colors.electricBlue : colors.secondaryText,
                cursor: 'pointer',
                minWidth: '32px',
                fontFamily: 'inherit',
              }}
            >
              {page}
            </button>
          )
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            padding: '4px 10px',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            border: `1px solid ${colors.mediumGray}`,
            borderRadius: '4px',
            backgroundColor: colors.white,
            color: currentPage === totalPages ? colors.placeholder : colors.secondaryText,
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            opacity: currentPage === totalPages ? 0.5 : 1,
            fontFamily: 'inherit',
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
}


// ─── COLLAPSIBLE SECTION ─────────────────────────────────────
export function CollapsibleSection({ title, indicator, children, defaultOpen }) {
  const [isOpen, setIsOpen] = useState(defaultOpen || false);

  return (
    <div style={{
      backgroundColor: colors.white,
      borderRadius: '12px',
      border: `1px solid ${colors.mediumGray}`,
      overflow: 'hidden',
    }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 24px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ color: colors.textMuted, fontSize: 'var(--font-size-xs)', width: '12px', flexShrink: 0, transition: 'transform 0.2s ease' }}>
          {isOpen ? '\u25BE' : '\u25B8'}
        </span>
        <span style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
          {title}
        </span>
        {indicator && (
          <span style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
            {indicator}
          </span>
        )}
      </button>
      <div style={{
        maxHeight: isOpen ? '2000px' : '0',
        overflow: 'hidden',
        transition: 'max-height 0.3s ease-out',
      }}>
        <div style={{ padding: '0 24px 24px' }}>
          {children}
        </div>
      </div>
    </div>
  );
}


// ─── STAT CARD (kept for backward compatibility, use sparingly) ─
// Directional delta arrow for StatCard (DESIGN.md section 7: "directional SVG
// arrow + semantic color. Never emoji arrows"). Renders only for up/down; stroke
// is currentColor so it inherits the semantic color set on the delta line. SVG
// per section 7 icon rule (1.4-1.6px stroke, currentColor). Replaces the prior
// Unicode arrow glyphs.
function StatCardTrendArrow({ direction }) {
  if (direction !== 'up' && direction !== 'down') return null;
  const d = direction === 'up'
    ? 'M6 10.5V3.5M3.3 6.2 6 3.5 8.7 6.2'   // shaft up + chevron head at top
    : 'M6 3.5V10.5M3.3 7.8 6 10.5 8.7 7.8'; // shaft down + chevron head at bottom
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d={d} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function StatCard({ label, value, subtext, icon, trend, trendDirection }) {
  return (
    <Card padding="var(--space-lg)">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          {/* Meridian stat-card label: 12px --color-text-muted (DESIGN.md section 7).
              No uppercase -- section 2 reserves uppercase for 11px column headers,
              not 12px captions. */}
          <div style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            marginBottom: 'var(--space-xs)',
            fontWeight: 'var(--font-weight-medium)',
          }}>
            {label}
          </div>
          {/* Meridian stat value: 24px, mono, tabular, weight 500, -0.02em (DESIGN.md
              section 7). Geist Mono is the target; the font swap is a LATER lane, so
              reference var(--font-mono) with the current mono fallback stack -- it
              renders monospace today and auto-adopts Geist Mono once that lane
              defines --font-mono. */}
          <div style={{
            fontSize: 'var(--font-size-hero)',
            fontFamily: "var(--font-mono, 'SF Mono', 'Fira Code', 'Fira Mono', monospace)",
            fontWeight: 'var(--font-weight-medium)',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: 'var(--letter-spacing-tight)',
            color: 'var(--color-text-primary)',
            lineHeight: 'var(--line-height-tight)',
          }}>
            {value}
          </div>
          {subtext && (
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginTop: '3px' }}>
              {subtext}
            </div>
          )}
          {trend && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              fontSize: 'var(--font-size-sm)',
              marginTop: 'var(--space-xs)',
              color: trendDirection === 'up'
                ? 'var(--color-success)'
                : trendDirection === 'down'
                  ? 'var(--color-danger)'
                  : 'var(--color-text-muted)',
              fontWeight: 'var(--font-weight-medium)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              <StatCardTrendArrow direction={trendDirection} />
              {trend}
            </div>
          )}
        </div>
        {icon && (
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--color-surface-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--font-size-base)',
          }}>
            {icon}
          </div>
        )}
      </div>
    </Card>
  );
}
